/**
 * Hook useAutoSave — Fase 14.
 *
 * Guarda automáticamente el estado del usuario (perfiles, overrides, threshold)
 * en el backend cada vez que cambia, con debounce de 2 segundos.
 * También guarda al perder el foco de la ventana (cambio de tab, alt+tab).
 *
 * Maneja conflict resolution (409): cuando el backend detecta que otro
 * tab guardó antes, el hook expone `resolveConflict` para que el usuario
 * decida entre "descartar mis cambios" o "sobreescribir".
 *
 * Reglas de activación:
 *   - Solo guarda si `currentProcessingIdAtom` es no-null.
 *   - NO guarda en el mount inicial (evita save redundante tras rehidratación).
 *   - `version` está excluida de los deps del debounce: cambiaría en cada
 *     save exitoso y causaría un loop. Se lee de `latestRef` al guardar.
 *
 * Uso:
 *   const { resolveConflict } = useAutoSave();
 *   // Pasar resolveConflict al componente SaveIndicator.
 */

import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useRef } from "react";
import {
  currentProcessingIdAtom,
  customSpeakersAtom,
  manualOverridesAtom,
  metaVersionAtom,
  profilesAtom,
  saveConflictAtom,
  saveStatusAtom,
  thresholdAtom,
} from "../../state/atoms";
import {
  ApiError,
  saveUserState,
  type SaveUserStateConflict,
  type WireSpeakerProfile,
} from "../../platform/api";

const DEBOUNCE_MS = 2000;
const SAVED_DISPLAY_MS = 3000;
const ERROR_DISPLAY_MS = 5000;

export function useAutoSave() {
  // --- Lectura de atoms ---
  const processingId = useAtomValue(currentProcessingIdAtom);
  const profiles = useAtomValue(profilesAtom);
  const customSpeakers = useAtomValue(customSpeakersAtom);
  const manualOverrides = useAtomValue(manualOverridesAtom);
  const threshold = useAtomValue(thresholdAtom);
  const version = useAtomValue(metaVersionAtom);

  // --- Setters ---
  const setSaveStatus = useSetAtom(saveStatusAtom);
  const setSaveConflict = useSetAtom(saveConflictAtom);
  const setMetaVersion = useSetAtom(metaVersionAtom);
  const setProfiles = useSetAtom(profilesAtom);
  const setCustomSpeakers = useSetAtom(customSpeakersAtom);
  const setManualOverrides = useSetAtom(manualOverridesAtom);
  const setThreshold = useSetAtom(thresholdAtom);

  // --- Refs ---
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const statusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasMountedRef = useRef(false);

  /**
   * latestRef siempre tiene los valores actuales para evitar stale closures
   * en callbacks que no se recrean en cada render.
   */
  const latestRef = useRef({
    profiles,
    customSpeakers,
    manualOverrides,
    threshold,
    version,
    processingId,
    conflict: null as SaveUserStateConflict | null,
  });
  latestRef.current = {
    ...latestRef.current,
    profiles,
    customSpeakers,
    manualOverrides,
    threshold,
    version,
    processingId,
  };

  // Leer el conflict del atom para mantener latestRef al día.
  const conflict = useAtomValue(saveConflictAtom);
  latestRef.current.conflict = conflict;

  // --- Core: ejecutar el guardado ---

  const performSave = useCallback(
    async (overrideVersion?: number) => {
      const { profiles, customSpeakers, manualOverrides, threshold, version, processingId } =
        latestRef.current;
      if (!processingId) return;

      if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
      setSaveStatus("saving");
      setSaveConflict(null);

      // Map<number, SpeakerProfile> → Record<number, WireSpeakerProfile>
      // Se descarta el campo `index` porque es redundante con la clave del Record.
      const speakerProfiles: Record<number, WireSpeakerProfile> = {};
      for (const [k, v] of profiles) {
        speakerProfiles[k] = { name: v.name, role: v.role };
      }

      // Map<number, number> → Record<number, number>
      const manualOverridesRecord: Record<number, number> = {};
      for (const [k, v] of manualOverrides) {
        manualOverridesRecord[k] = v;
      }

      try {
        const res = await saveUserState(processingId, {
          version: overrideVersion ?? version,
          speakerProfiles,
          customSpeakers,
          manualOverrides: manualOverridesRecord,
          threshold,
        });
        // Actualizar la versión local para que el próximo save la use.
        latestRef.current.version = res.version;
        setMetaVersion(res.version);
        setSaveStatus("saved");
        statusTimerRef.current = setTimeout(() => setSaveStatus("idle"), SAVED_DISPLAY_MS);
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          const conflictData = err.body as SaveUserStateConflict;
          setSaveConflict(conflictData);
          setSaveStatus("error");
          // El status "error" se queda hasta que el usuario resuelva el conflicto.
        } else {
          setSaveStatus("error");
          statusTimerRef.current = setTimeout(() => setSaveStatus("idle"), ERROR_DISPLAY_MS);
        }
      }
    },
    [setSaveStatus, setSaveConflict, setMetaVersion]
  );

  // --- Debounce: programar save al cambiar atoms relevantes ---

  useEffect(() => {
    // Saltar el primer mount para no hacer un save redundante tras la
    // rehidratación de atoms desde el backend.
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    if (!processingId) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => void performSave(), DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // `version` excluido deliberadamente: se actualiza en cada save exitoso
    // y causaría un loop. Se lee de latestRef.current.version al guardar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profiles, customSpeakers, manualOverrides, threshold, processingId]);

  // --- On-blur: guardar al perder el foco de la ventana ---

  useEffect(() => {
    if (!processingId) return;
    const handleBlur = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      void performSave();
    };
    window.addEventListener("blur", handleBlur);
    return () => window.removeEventListener("blur", handleBlur);
  }, [processingId, performSave]);

  // --- Cleanup al desmontar ---

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    };
  }, []);

  // --- Conflict resolution ---

  const resolveConflict = useCallback(
    async (action: "discard" | "overwrite") => {
      const { conflict, processingId } = latestRef.current;
      if (!conflict) return;
      if (statusTimerRef.current) clearTimeout(statusTimerRef.current);

      if (action === "discard") {
        // Cargar el estado del servidor en los atoms locales.
        const { currentState, currentVersion } = conflict;

        if (currentState.speakerProfiles) {
          const profilesMap = new Map(
            Object.entries(currentState.speakerProfiles).map(([k, v]) => {
              const idx = Number(k);
              return [idx, { ...v, index: idx }];
            })
          );
          setProfiles(profilesMap);
        }

        if (currentState.customSpeakers) {
          setCustomSpeakers(currentState.customSpeakers);
        }

        if (currentState.manualOverrides) {
          const overridesMap = new Map(
            Object.entries(currentState.manualOverrides).map(([k, v]) => [
              Number(k),
              Number(v),
            ])
          );
          setManualOverrides(overridesMap);
        }

        if (currentState.threshold !== undefined) {
          setThreshold(currentState.threshold);
        }

        // Actualizar versión local para que el próximo save use la del servidor.
        latestRef.current.version = currentVersion;
        setMetaVersion(currentVersion);
        setSaveConflict(null);
        setSaveStatus("idle");
      } else {
        // Sobreescribir: guardar con la versión del servidor (pisa sus cambios).
        if (!processingId) return;
        const serverVersion = conflict.currentVersion;
        // Actualizar latestRef antes de performSave para que lo lea correctamente.
        latestRef.current.version = serverVersion;
        await performSave(serverVersion);
      }
    },
    [
      performSave,
      setMetaVersion,
      setProfiles,
      setCustomSpeakers,
      setManualOverrides,
      setThreshold,
      setSaveConflict,
      setSaveStatus,
    ]
  );

  return { resolveConflict };
}
